import type { CameraPath, CameraPathAuthoring, PathPoint, PathVec3 } from "@/lib/location-scene-3d/camera-path"
import { normalizeSpeedProfile, type CameraSpeedProfile } from "@/lib/location-scene-3d/camera-speed"
import {
  bakeSplinePath,
  clampSplineDuration,
  curvePolyline,
  DEFAULT_SPLINE_DURATION_MS,
  MIN_SPLINE_DURATION_MS,
} from "@/lib/location-scene-3d/camera-path-spline"

export const OBJECT_PATH_VERSION = 1

export type ObjectPathSample = {
  t: number
  pos: PathVec3
}

export type ObjectPathAuthoring = {
  smooth: boolean
  points: PathPoint[]
}

/** Позиция по сплайну. Без взгляда и поворота. Скорость — тот же профиль, что у пролёта. */
export type ObjectPath = {
  version: number
  duration_ms: number
  samples: ObjectPathSample[]
  authoring?: ObjectPathAuthoring
  speed?: CameraSpeedProfile
}

export type ObjectPathCarrier = {
  uid: string
  group_id?: string
  position: PathVec3
  path?: ObjectPath
}

export function emptyObjectPath(): ObjectPath {
  return { version: OBJECT_PATH_VERSION, duration_ms: 0, samples: [] }
}

function copyPoint(point: PathPoint): PathPoint {
  return {
    pos: [...point.pos] as PathVec3,
    tangent: point.tangent ? ([...point.tangent] as PathVec3) : null,
  }
}

function copySpeed(speed: CameraSpeedProfile | null | undefined, durationMs: number): CameraSpeedProfile | null {
  if (!speed) return null
  return normalizeSpeedProfile(speed, durationMs)
}

export function cloneObjectPath(path: ObjectPath): ObjectPath {
  const speed = copySpeed(path.speed, path.duration_ms)
  return {
    version: path.version,
    duration_ms: path.duration_ms,
    samples: path.samples.map((sample) => ({ t: sample.t, pos: [...sample.pos] as PathVec3 })),
    authoring: path.authoring
      ? {
          smooth: path.authoring.smooth,
          points: path.authoring.points.map(copyPoint),
        }
      : undefined,
    ...(speed ? { speed } : {}),
  }
}

export function objectPathDurationMs(path: ObjectPath | null | undefined): number {
  if (!path) return 0
  if (path.duration_ms > 0) return path.duration_ms
  return path.samples[path.samples.length - 1]?.t ?? 0
}

export function objectPathHasContent(path: ObjectPath | null | undefined): boolean {
  if (!path) return false
  if (path.samples.length >= 2) return true
  return (path.authoring?.points.length ?? 0) >= 2
}

function isVec3(raw: unknown): raw is PathVec3 {
  return (
    Array.isArray(raw) && raw.length === 3 && raw.every((value) => typeof value === "number" && Number.isFinite(value))
  )
}

function normalizePoint(raw: unknown): PathPoint | null {
  if (!raw || typeof raw !== "object") return null
  const row = raw as { pos?: unknown; position?: unknown; tangent?: unknown }
  const pos = isVec3(row.pos) ? row.pos : isVec3(row.position) ? row.position : null
  if (!pos) return null
  let tangent: PathVec3 | null = null
  if (row.tangent != null) {
    if (!isVec3(row.tangent)) return { pos: [...pos] as PathVec3, tangent: null }
    tangent = [...row.tangent] as PathVec3
  }
  return { pos: [...pos] as PathVec3, tangent }
}

/** Смотрит pos и speed. look, yaw и fov выкидывает. */
export function normalizeObjectPath(raw: unknown): ObjectPath | undefined {
  if (!raw || typeof raw !== "object") return undefined
  const row = raw as {
    version?: unknown
    duration_ms?: unknown
    samples?: unknown
    authoring?: unknown
    speed?: unknown
  }
  const samples: ObjectPathSample[] = []
  if (Array.isArray(row.samples)) {
    for (const item of row.samples) {
      if (!item || typeof item !== "object") continue
      const sample = item as { t?: unknown; pos?: unknown; position?: unknown }
      const pos = isVec3(sample.pos) ? sample.pos : isVec3(sample.position) ? sample.position : null
      const t = typeof sample.t === "number" && Number.isFinite(sample.t) ? sample.t : null
      if (!pos || t == null) continue
      samples.push({ t, pos: [...pos] as PathVec3 })
    }
  }
  let authoring: ObjectPathAuthoring | undefined
  if (row.authoring && typeof row.authoring === "object") {
    const body = row.authoring as { smooth?: unknown; points?: unknown }
    const points: PathPoint[] = []
    if (Array.isArray(body.points)) {
      for (const item of body.points) {
        const point = normalizePoint(item)
        if (point) points.push(point)
      }
    }
    if (points.length > 0) authoring = { smooth: body.smooth !== false, points }
  }
  if (samples.length === 0 && !authoring) return undefined
  const duration = typeof row.duration_ms === "number" && Number.isFinite(row.duration_ms) ? row.duration_ms : 0
  const version = typeof row.version === "number" && row.version > 0 ? Math.round(row.version) : OBJECT_PATH_VERSION
  const speed = normalizeSpeedProfile(row.speed, duration)
  return {
    version,
    duration_ms: duration,
    samples,
    ...(authoring ? { authoring } : {}),
    ...(speed ? { speed } : {}),
  }
}

export function bakeObjectPath(
  points: readonly PathPoint[],
  durationMs: number,
  speed?: CameraSpeedProfile | null,
): ObjectPath {
  const duration = clampSplineDuration(durationMs)
  const profile = speed ? normalizeSpeedProfile(speed, duration) : null
  const baked = bakeSplinePath(
    { smooth: true, look: { mode: "tangent" }, points: points.map(copyPoint) },
    { durationMs: duration, fov: 55, speed: profile },
  )
  return {
    version: OBJECT_PATH_VERSION,
    duration_ms: baked.duration_ms,
    samples: baked.samples.map((sample) => ({ t: sample.t, pos: [...sample.pos] as PathVec3 })),
    authoring: baked.authoring
      ? { smooth: true, points: baked.authoring.points.map(copyPoint) }
      : { smooth: true, points: points.map(copyPoint) },
    ...(profile ? { speed: profile } : {}),
  }
}

export function shiftObjectPath(path: ObjectPath, delta: PathVec3): ObjectPath {
  const move = (pos: PathVec3): PathVec3 => [pos[0] + delta[0], pos[1] + delta[1], pos[2] + delta[2]]
  const speed = copySpeed(path.speed, path.duration_ms)
  return {
    version: path.version,
    duration_ms: path.duration_ms,
    samples: path.samples.map((sample) => ({ t: sample.t, pos: move(sample.pos) })),
    authoring: path.authoring
      ? {
          smooth: path.authoring.smooth,
          points: path.authoring.points.map((point) => ({
            pos: move(point.pos),
            tangent: point.tangent ? ([...point.tangent] as PathVec3) : null,
          })),
        }
      : undefined,
    ...(speed ? { speed } : {}),
  }
}

/** Держит первую точку до старта и последнюю после конца. */
export function sampleObjectPath(path: ObjectPath | null | undefined, ms: number): PathVec3 | null {
  if (!path) return null
  const samples = path.samples
  if (samples.length === 0) {
    const pos = path.authoring?.points[0]?.pos
    return pos ? ([...pos] as PathVec3) : null
  }
  if (samples.length === 1 || ms <= samples[0].t) return [...samples[0].pos] as PathVec3
  const duration = objectPathDurationMs(path)
  const t = Math.min(Math.max(ms, 0), duration > 0 ? duration : samples[samples.length - 1].t)
  const last = samples[samples.length - 1]
  if (t >= last.t) return [...last.pos] as PathVec3
  let index = 1
  while (index < samples.length && samples[index].t < t) index += 1
  const a = samples[index - 1]
  const b = samples[index] ?? last
  const span = b.t - a.t
  const share = span > 1e-6 ? (t - a.t) / span : 0
  return [
    a.pos[0] + (b.pos[0] - a.pos[0]) * share,
    a.pos[1] + (b.pos[1] - a.pos[1]) * share,
    a.pos[2] + (b.pos[2] - a.pos[2]) * share,
  ]
}

export function objectPathStart(path: ObjectPath | null | undefined): PathVec3 | null {
  const pos = path?.authoring?.points[0]?.pos ?? path?.samples[0]?.pos
  return pos ? ([...pos] as PathVec3) : null
}

/** Оверлей камеры рисует путь объекта без фрустума. */
export function objectPathAsCameraPath(path: ObjectPath): CameraPath {
  const authoring: CameraPathAuthoring | undefined = path.authoring
    ? { smooth: path.authoring.smooth, look: { mode: "tangent" }, points: path.authoring.points.map(copyPoint) }
    : undefined
  return {
    version: 1,
    duration_ms: path.duration_ms,
    samples: path.samples.map((sample) => ({
      t: sample.t,
      pos: [...sample.pos] as PathVec3,
      yaw: 0,
      pitch: 0,
      fov: 55,
    })),
    ...(authoring ? { authoring } : {}),
  }
}

export function objectPathPolyline(path: ObjectPath): PathVec3[] {
  const points = path.authoring?.points
  if (points && points.length >= 2 && path.authoring) {
    const authoring: CameraPathAuthoring = {
      smooth: path.authoring.smooth,
      look: { mode: "tangent" },
      points,
    }
    return curvePolyline(authoring).map((sample) => sample.pos)
  }
  return path.samples.map((sample) => [...sample.pos] as PathVec3)
}

export function defaultObjectPathDuration(path: ObjectPath | null | undefined, cameraDurationMs: number): number {
  if (path && path.duration_ms >= MIN_SPLINE_DURATION_MS) return clampSplineDuration(path.duration_ms)
  if (cameraDurationMs >= MIN_SPLINE_DURATION_MS) return clampSplineDuration(cameraDurationMs)
  return DEFAULT_SPLINE_DURATION_MS
}

export function sceneClockMs(cameraDurationMs: number, objects: readonly { path?: ObjectPath | null }[]): number {
  let max = cameraDurationMs > 0 ? cameraDurationMs : 0
  for (const obj of objects) {
    if (!objectPathHasContent(obj.path)) continue
    max = Math.max(max, objectPathDurationMs(obj.path))
  }
  return max
}

export function sceneHasMotion(cameraPath: CameraPath, objects: readonly { path?: ObjectPath | null }[]): boolean {
  if ((cameraPath.samples?.length ?? 0) >= 2) return true
  return objects.some((obj) => objectPathHasContent(obj.path))
}

export type ObjectPathFollow = {
  ownerUid: string
  offset: PathVec3
}

/**
 * Свой путь — объект едет сам. В группе с ровно одним путём остальные держат смещение покоя.
 * Несколько путей в группе: деталь без пути стоит.
 */
export function objectPathFollows(objects: readonly ObjectPathCarrier[]): Map<string, ObjectPathFollow> {
  const grouped = new Map<string, ObjectPathCarrier[]>()
  for (const obj of objects) {
    if (!obj.group_id) continue
    const list = grouped.get(obj.group_id) ?? []
    list.push(obj)
    grouped.set(obj.group_id, list)
  }
  const soleOwner = new Map<string, string>()
  for (const [groupId, members] of grouped) {
    const owners = members.filter((member) => objectPathHasContent(member.path))
    if (owners.length === 1) soleOwner.set(groupId, owners[0].uid)
  }
  const follows = new Map<string, ObjectPathFollow>()
  for (const obj of objects) {
    if (objectPathHasContent(obj.path)) {
      follows.set(obj.uid, { ownerUid: obj.uid, offset: [0, 0, 0] })
      continue
    }
    if (!obj.group_id) continue
    const ownerUid = soleOwner.get(obj.group_id)
    if (!ownerUid) continue
    const owner = objects.find((item) => item.uid === ownerUid)
    if (!owner) continue
    follows.set(obj.uid, {
      ownerUid,
      offset: [
        obj.position[0] - owner.position[0],
        obj.position[1] - owner.position[1],
        obj.position[2] - owner.position[2],
      ],
    })
  }
  return follows
}

export function posedObjectPosition(
  obj: ObjectPathCarrier,
  ms: number,
  objects: readonly ObjectPathCarrier[],
  follows: ReadonlyMap<string, ObjectPathFollow>,
): PathVec3 {
  const follow = follows.get(obj.uid)
  if (!follow) return [...obj.position] as PathVec3
  const owner = objects.find((item) => item.uid === follow.ownerUid)
  const sample = sampleObjectPath(owner?.path, ms)
  if (!sample) return [...obj.position] as PathVec3
  return [sample[0] + follow.offset[0], sample[1] + follow.offset[1], sample[2] + follow.offset[2]]
}
